import type React from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
    ChevronDown,
    Clock,
    Filter,
    Loader2,
    Megaphone,
    Search,
    Trash2,
} from "lucide-react";
import type { AnnouncementHistory } from "./types";
import { format, formatDistanceToNow } from "date-fns";
import { Button } from "../../components/misc/Button";
import ReconfirmOverlay from "../../components/misc/ReconfirmOverlay";
import RichTextViewer from "../../components/misc/rich-text-editor/RichTextViewer";

const isSafeHttpUrl = (url: string): boolean => {
    try {
        const { protocol } = new URL(url);
        return protocol === "http:" || protocol === "https:";
    } catch {
        return false;
    }
};

interface HistoryProps {
    activeTab: string;
    search: string;
    setSearch: (search: string) => void;
    isFilterOpen: boolean;
    setIsFilterOpen: (isOpen: boolean) => void;
    filterType: string | null;
    setFilterType: (type: string | null) => void;
    getTypeIcon: (type: string) => React.ReactNode;
    historyLoading: boolean;
    filteredHistory: AnnouncementHistory[];
    setExpandedId: (id: string | null) => void;
    expandedId: string | null;
    setDeleteId: (id: string) => void;
    setIsReconfirmOpen: (isOpen: boolean) => void;
    cancelDelete: () => void;
    confirmDelete: () => void;
    getTypeColor: (type: string) => string;
    history: AnnouncementHistory[];
    total: number;
    page: number;
    setPage: (page: number) => void;
    limit: number;
    isReconfirmOpen: boolean;
}

const History: React.FC<HistoryProps> = ({
    activeTab,
    search,
    setSearch,
    isFilterOpen,
    setIsFilterOpen,
    filterType,
    setFilterType,
    getTypeIcon,
    historyLoading,
    filteredHistory,
    setExpandedId,
    expandedId,
    setDeleteId,
    setIsReconfirmOpen,
    cancelDelete,
    confirmDelete,
    getTypeColor,
    history,
    total,
    page,
    setPage,
    limit,
    isReconfirmOpen,
}) => {
    const sortedHistory = [...filteredHistory].sort(
        (a, b) => new Date(b.sentAt).getTime() - new Date(a.sentAt).getTime()
    );
    return (
        <AnimatePresence mode="wait">
            {activeTab === "history" && (
                <motion.div
                    key="history"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ duration: 0.3 }}
                >
                    {/* History Section */}
                    <div className="bg-white rounded-xl shadow-md border border-gray-200 overflow-hidden">
                        <div className="p-6 border-b border-gray-100 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                            <div>
                                <h2 className="text-xl font-semibold text-gray-800 flex items-center">
                                    <Clock className="w-5 h-5 mr-2 text-secondary" />
                                    Announcement History
                                </h2>
                                <p className="text-gray-500 text-sm mt-1">
                                    View and manage past announcements
                                </p>
                            </div>

                            <div className="flex flex-col sm:flex-row gap-3">
                                <div className="relative">
                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                        <Search className="h-4 w-4 text-gray-400" />
                                    </div>
                                    <input
                                        value={search}
                                        onChange={(e) =>
                                            setSearch(e.target.value)
                                        }
                                        placeholder="Search announcements..."
                                        className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-secondary"
                                    />
                                </div>

                                <div className="relative">
                                    <button
                                        onClick={() =>
                                            setIsFilterOpen(!isFilterOpen)
                                        }
                                        className="flex items-center justify-between w-full px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-secondary"
                                    >
                                        <div className="flex items-center">
                                            <Filter className="w-4 h-4 mr-2 text-gray-500" />
                                            {filterType ? (
                                                <span className="capitalize">
                                                    {filterType}
                                                </span>
                                            ) : (
                                                <span>All Types</span>
                                            )}
                                        </div>
                                        <ChevronDown className="w-4 h-4 ml-2" />
                                    </button>

                                    {isFilterOpen && (
                                        <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg z-10 border border-gray-200">
                                            <div className="py-1">
                                                <button
                                                    onClick={() => {
                                                        setFilterType(null);
                                                        setIsFilterOpen(false);
                                                    }}
                                                    className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 w-full text-left"
                                                >
                                                    All Types
                                                </button>
                                                {["announcement"].map(
                                                    (type) => (
                                                        <button
                                                            key={type}
                                                            onClick={() => {
                                                                setFilterType(
                                                                    type
                                                                );
                                                                setIsFilterOpen(
                                                                    false
                                                                );
                                                            }}
                                                            className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 w-full text-left"
                                                        >
                                                            <div className="flex items-center">
                                                                <div className="mr-2">
                                                                    {getTypeIcon(
                                                                        type
                                                                    )}
                                                                </div>
                                                                <span className="capitalize">
                                                                    {type}
                                                                </span>
                                                            </div>
                                                        </button>
                                                    )
                                                )}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        {historyLoading ? (
                            <div className="flex flex-col items-center justify-center py-16">
                                <Loader2 className="w-12 h-12 text-primary animate-spin mb-4" />
                                <p className="text-gray-500 font-medium">
                                    Loading announcement history...
                                </p>
                            </div>
                        ) : sortedHistory.length === 0 ? (
                            <div className="flex flex-col items-center justify-center py-16 px-4 text-center">
                                <div className="bg-gray-100 p-4 rounded-full mb-4">
                                    <Megaphone className="w-12 h-12 text-gray-400" />
                                </div>
                                <h3 className="text-lg font-medium text-gray-800 mb-1">
                                    No announcements found
                                </h3>
                                <p className="text-gray-500 max-w-md">
                                    {search || filterType
                                        ? "Try adjusting your search or filter to find what you're looking for."
                                        : "When you send announcements, they will appear here."}
                                </p>
                            </div>
                        ) : (
                            <div>
                                <table className="min-w-full divide-y divide-gray-200">
                                    <thead className="bg-gray-50">
                                        <tr>
                                            <th
                                                scope="col"
                                                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                                            >
                                                Announcement
                                            </th>
                                            <th
                                                scope="col"
                                                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                                            >
                                                Type
                                            </th>
                                            <th
                                                scope="col"
                                                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                                            >
                                                Sent
                                            </th>
                                            <th
                                                scope="col"
                                                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                                            >
                                                Status
                                            </th>
                                            <th
                                                scope="col"
                                                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                                            >
                                                Actions
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-gray-200">
                                        {sortedHistory.map((announcement) => (
                                            <tr
                                                key={announcement._id}
                                                className="hover:bg-gray-50 transition-colors"
                                            >
                                                <td
                                                    className="px-6 py-4 cursor-pointer"
                                                    onClick={() =>
                                                        setExpandedId(
                                                            expandedId ===
                                                                announcement._id
                                                                ? null
                                                                : announcement._id
                                                        )
                                                    }
                                                >
                                                    <div className="text-sm font-medium text-gray-900 mb-1">
                                                        {announcement.title}
                                                    </div>
                                                    <div
                                                        className={
                                                            expandedId ===
                                                            announcement._id
                                                                ? "text-sm text-gray-500 whitespace-normal max-w-none"
                                                                : "text-sm text-gray-500 truncate max-w-xs"
                                                        }
                                                    >
                                                        {expandedId ===
                                                            announcement._id && (
                                                            <RichTextViewer
                                                                content={
                                                                    announcement.message
                                                                }
                                                            />
                                                        )}
                                                    </div>
                                                    {announcement.url && (
                                                        <div className="mt-2">
                                                            <span className="text-gray-500 text-xs">
                                                                Link:{" "}
                                                                {isSafeHttpUrl(
                                                                    announcement.url
                                                                ) ? (
                                                                    <a
                                                                        href={
                                                                            announcement.url
                                                                        }
                                                                        target="_blank"
                                                                        rel="noopener noreferrer"
                                                                        className="text-secondary hover:text-primary hover:underline"
                                                                        onClick={(
                                                                            e
                                                                        ) =>
                                                                            e.stopPropagation()
                                                                        }
                                                                    >
                                                                        {
                                                                            announcement.url
                                                                        }
                                                                    </a>
                                                                ) : (
                                                                    <span className="break-all">
                                                                        {
                                                                            announcement.url
                                                                        }
                                                                    </span>
                                                                )}
                                                            </span>
                                                        </div>
                                                    )}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <span
                                                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getTypeColor(
                                                            announcement.type
                                                        )}`}
                                                    >
                                                        {getTypeIcon(
                                                            announcement.type
                                                        )}
                                                        <span className="ml-1.5 capitalize">
                                                            {announcement.type}
                                                        </span>
                                                    </span>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <div className="text-sm text-gray-900">
                                                        {formatDistanceToNow(
                                                            new Date(
                                                                announcement.sentAt
                                                            ),
                                                            {
                                                                addSuffix: true,
                                                            }
                                                        )}
                                                    </div>
                                                    <div className="text-xs text-gray-500">
                                                        {format(
                                                            new Date(
                                                                announcement.sentAt
                                                            ),
                                                            "MMM d, yyyy 'at' h:mm a"
                                                        )}
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <span
                                                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium`}
                                                    >
                                                        {announcement.status}
                                                    </span>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                                    <Button
                                                        variant="destructive"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            setDeleteId(
                                                                announcement._id
                                                            );
                                                            setIsReconfirmOpen(
                                                                true
                                                            );
                                                        }}
                                                        disabled={
                                                            announcement.status.toLowerCase() ===
                                                                "expired" ||
                                                            announcement.status.toLowerCase() ===
                                                                "deleted"
                                                        }
                                                    >
                                                        <Trash2 className="w-4 h-4" />
                                                    </Button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}

                        <div className="bg-gray-50 px-6 py-4 border-t border-gray-200 flex items-center justify-between">
                            {/* Showing X of Y */}
                            <div className="text-sm text-gray-500">
                                Showing{" "}
                                <span className="font-medium">
                                    {history.length}
                                </span>{" "}
                                of <span className="font-medium">{total}</span>{" "}
                                announcements
                            </div>

                            {/* Prev / Page / Next */}
                            <div className="flex items-center space-x-2">
                                <Button
                                    onClick={() =>
                                        setPage(Math.max(page - 1, 1))
                                    }
                                    disabled={page === 1}
                                >
                                    Prev
                                </Button>
                                <span className="text-sm text-gray-700">
                                    Page {page}
                                </span>
                                <Button
                                    onClick={() => setPage(page + 1)}
                                    disabled={page * limit >= total}
                                >
                                    Next
                                </Button>
                            </div>
                        </div>
                    </div>
                    <ReconfirmOverlay
                        isOpen={isReconfirmOpen}
                        onClose={cancelDelete}
                        onConfirm={confirmDelete}
                        title="Delete Notification"
                        message="Are you sure you want to delete the notification?"
                        messageTwo={`This will delete the notification for all users and cannot be undone.`}
                        confirmText="Delete"
                    />
                </motion.div>
            )}
        </AnimatePresence>
    );
};

export default History;
